/**
 * Tenant geofence-mode settings hooks (Phase 8 / Task 8.2, DEC-080).
 *
 *   GET   /v1/tenants/settings  → { settings: { geofenceMode } }   (DISPATCHER+)
 *   PATCH /v1/tenants/settings  → { ok: true; settings }            (DISPATCH_MANAGER+)
 *
 * A plain DISPATCHER reading is allowed; only DISPATCH_MANAGER+ may PATCH (a
 * DISPATCHER PATCH returns 403).
 */

import { useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import type { TenantGeofenceMode } from '@lodex-pro/shared';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

export const TENANT_SETTINGS_QUERY_KEY = ['tenant', 'settings'] as const;

/**
 * GET / PATCH /v1/tenants/settings. OPEN-220a / DEC-386 — autoArrivalPickup / autoArrivalDisposal:
 * may a geofence mark arrival, per kind (both OFF by default).
 */
export interface TenantSettings {
  geofenceMode: TenantGeofenceMode;
  autoArrivalPickup: boolean;
  autoArrivalDisposal: boolean;
}

export interface TenantSettingsResponse {
  settings: TenantSettings;
}

export function useTenantSettings(): UseQueryResult<TenantSettingsResponse> {
  const opts = useApiOpts();
  return useApiQuery<TenantSettingsResponse>(opts, '/v1/tenants/settings', {
    queryKey: TENANT_SETTINGS_QUERY_KEY,
    staleTime: 60_000,
  });
}

/** Any of the three, at least one (the server refuses an empty body). */
export type SaveTenantSettingsBody = Partial<TenantSettings>;

export interface SaveTenantSettingsResponse {
  ok: true;
  settings: TenantSettings;
}

export function useSaveTenantSettings(): UseMutationResult<
  SaveTenantSettingsResponse,
  Error,
  SaveTenantSettingsBody
> {
  const opts = useApiOpts();
  const queryClient = useQueryClient();
  return useApiMutation<SaveTenantSettingsBody, SaveTenantSettingsResponse>(
    opts,
    '/v1/tenants/settings',
    'PATCH',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: TENANT_SETTINGS_QUERY_KEY });
      },
    },
  );
}
